import React, { useState, useEffect, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeQAValidation,
  handleQAInputChangeWithValidation,
  handleQAResetWithValidation,
} from "../../../utils/formValidation";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Q&A", path: `/qa` },
];

// ソート可能なQ&Aアイテムコンポーネント
function SortableQAItem({
  qa_item,
  qaIndex,
  categoryIndex,
  handleInputChange,
  handleDeleteQA,
  handleAddQA,
  handleImageButtonClick,
  handleAddLink,
  handleDeleteLink,
  handleUrlFocus,
  handleUrlInputChange,
  handleQAImagesChange,
  handleStudioToggle,
  imageFormVisible,
  studioList,
  isDragging: globalIsDragging,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: qa_item.id || `qa-${categoryIndex}-${qaIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="nest-item">
      {/* 追加削除 */}
      <div className="left ctrl">
        <div
          className="drag-handle"
          {...attributes}
          {...listeners}
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            padding: "2px",
            marginRight: "5px",
          }}
        >
          <p>{qaIndex + 1}</p>
        </div>

        {/* QA追加削除 */}
        <div className="icon-wrap">
          <i
            className="icon delete"
            onClick={() => handleDeleteQA(categoryIndex, qaIndex)}
            style={{ cursor: "pointer" }}
          ></i>
          <i
            className="icon add"
            onClick={() => handleAddQA(categoryIndex, qaIndex)}
            style={{ cursor: "pointer" }}
          ></i>
        </div>
      </div>
      {/* フォーム */}
      <div className="right">
        {/* Q */}
        <input
          type="text"
          id={`question_${categoryIndex}_${qaIndex}`}
          name={`question_${categoryIndex}_${qaIndex}`}
          className="q"
          value={qa_item.q || qa_item.question || ""}
          onChange={(e) => handleInputChange(e, categoryIndex, qaIndex, "q")}
          onFocus={(e) => handleInputChange(e, categoryIndex, qaIndex, "q")}
          placeholder="質問を入力"
          data-label="質問"
          data-required="true"
        />
        {/* A */}
        <textarea
          id={`answer_${categoryIndex}_${qaIndex}`}
          name={`answer_${categoryIndex}_${qaIndex}`}
          className="a"
          value={qa_item.a || qa_item.answer || ""}
          onChange={(e) => handleInputChange(e, categoryIndex, qaIndex, "a")}
          onFocus={(e) => handleInputChange(e, categoryIndex, qaIndex, "a")}
          placeholder="回答を入力"
          data-label="回答"
          data-required="true"
        />

        {/* 画像選択フォーム（条件付き表示） */}
        {(imageFormVisible[`${categoryIndex}_${qaIndex}`] ||
          (qa_item.images && qa_item.images.length > 0)) && (
          <div className="input3 img-form">
            <MediaSelector
              label="トップ画像"
              aspectRatio="16:9"
              type="image"
              fieldName={`qa_item_${categoryIndex}_${qaIndex}_images`}
              selectedMedia={qa_item.images || []}
              onSelect={(images) =>
                handleQAImagesChange(categoryIndex, qaIndex, images)
              }
              mode="multiple"
            />
          </div>
        )}

        {/* スタジオ選択ボタン */}
        <div className="studio-selection">
          <h5>スタジオページにも表示</h5>
          <div className="studio-buttons">
            <button
              type="button"
              className={`studio-btn ${
                (qa_item.studio_ids || []).includes(0) ? "selected" : ""
              }`}
              onClick={() => handleStudioToggle(categoryIndex, qaIndex, 0)}
            >
              全店舗
            </button>
            {studioList.map((studio) => (
              <button
                key={studio.id}
                type="button"
                className={`studio-btn ${
                  (qa_item.studio_ids || []).includes(studio.id)
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  handleStudioToggle(categoryIndex, qaIndex, studio.id)
                }
              >
                {studio.name}
              </button>
            ))}
          </div>
        </div>

        {/* URL（動的） */}
        <dl className="urls txt-form">
          {(qa_item.urls || []).map((urlItem, linkIndex) => (
            <React.Fragment key={linkIndex}>
              <dt>
                <input
                  type="text"
                  id={`link_text_${linkIndex}_${categoryIndex}_${qaIndex}`}
                  name={`link_text_${linkIndex}_${categoryIndex}_${qaIndex}`}
                  className="q"
                  value={urlItem.text || ""}
                  onChange={(e) =>
                    handleUrlInputChange(
                      e,
                      categoryIndex,
                      qaIndex,
                      linkIndex,
                      "text"
                    )
                  }
                  onFocus={() => handleUrlFocus(categoryIndex, qaIndex)}
                  placeholder={"リンクテキストを入力"}
                />
              </dt>
              <dd
                onClick={(e) => {
                  if (e.target.tagName !== "INPUT") {
                    handleDeleteLink(categoryIndex, qaIndex, linkIndex);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <input
                  type="text"
                  id={`link_url_${linkIndex}_${categoryIndex}_${qaIndex}`}
                  name={`link_url_${linkIndex}_${categoryIndex}_${qaIndex}`}
                  className="q"
                  value={urlItem.url || ""}
                  onChange={(e) =>
                    handleUrlInputChange(
                      e,
                      categoryIndex,
                      qaIndex,
                      linkIndex,
                      "url"
                    )
                  }
                  onFocus={() => handleUrlFocus(categoryIndex, qaIndex)}
                  placeholder={"URLを入力"}
                />
              </dd>
            </React.Fragment>
          ))}
        </dl>

        <div className="accent-wrap">
          {/* +Imageボタン（画像が0枚かつimg-formが非表示の場合のみ表示） */}
          {!imageFormVisible[`${categoryIndex}_${qaIndex}`] &&
            (!qa_item.images || qa_item.images.length === 0) && (
              <p
                onClick={() => handleImageButtonClick(categoryIndex, qaIndex)}
                style={{ cursor: "pointer" }}
              >
                <i className="icon add"></i>
                <span>Image</span>
              </p>
            )}
          <p
            onClick={() => handleAddLink(categoryIndex, qaIndex)}
            style={{ cursor: "pointer" }}
          >
            <i className="icon add"></i>
            <span>Link</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ソート可能なセクションコンポーネント
function SortableQASection({
  item,
  index,
  handleInputChange,
  handleDeleteGroup,
  handleAddGroup,
  handleDeleteQA,
  handleAddQA,
  handleImageButtonClick,
  handleAddLink,
  handleDeleteLink,
  handleUrlFocus,
  handleUrlInputChange,
  handleQAImagesChange,
  handleStudioToggle,
  pageContent,
  imageFormVisible,
  studioList,
  isDragging: globalIsDragging,
  handleQADragEnd,
  handleQADragStart,
  isQADragging,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id || `section-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // QAアイテムのセンサー
  const qaSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <section ref={setNodeRef} style={style}>
      <div className="area4">
        <div className="item">
          <div className="section-header">
            <div
              className="drag-handle"
              {...attributes}
              {...listeners}
              style={{
                cursor: isDragging ? "grabbing" : "grab",
              }}
            >
              <span style={{ fontSize: "14px", color: "#666" }}>
                #{index + 1}
              </span>
            </div>
          </div>
          {/* カテゴリ */}
          <input
            type="text"
            id={`category_${index}`}
            name={`category_${index}`}
            value={item.category || item.title || ""}
            onChange={(e) => handleInputChange(e, index, null, "category")}
            onFocus={(e) => handleInputChange(e, index, null, "category")}
            placeholder="カテゴリー名を入力"
            data-label="カテゴリ名"
            data-required="true"
          />
          <div
            className="nest-item-wrap edit-area"
            key={index}
            style={{
              overflow: "hidden",
            }}
          >
            {/* QA繰り返し */}
            {!globalIsDragging && (
              <DndContext
                sensors={qaSensors}
                collisionDetection={closestCenter}
                onDragStart={handleQADragStart}
                onDragEnd={(event) => handleQADragEnd(event, index)}
              >
                <SortableContext
                  items={(item.qa_items || []).map(
                    (qa_item, idx) => qa_item.id || `qa-${index}-${idx}`
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  {(item.qa_items || []).map((qa_item, idx) => (
                    <SortableQAItem
                      key={qa_item.id || `qa-${index}-${idx}`}
                      qa_item={qa_item}
                      qaIndex={idx}
                      categoryIndex={index}
                      handleInputChange={handleInputChange}
                      handleDeleteQA={handleDeleteQA}
                      handleAddQA={handleAddQA}
                      handleImageButtonClick={handleImageButtonClick}
                      handleAddLink={handleAddLink}
                      handleDeleteLink={handleDeleteLink}
                      handleUrlFocus={handleUrlFocus}
                      handleUrlInputChange={handleUrlInputChange}
                      handleQAImagesChange={handleQAImagesChange}
                      handleStudioToggle={handleStudioToggle}
                      imageFormVisible={imageFormVisible}
                      studioList={studioList}
                      isDragging={globalIsDragging}
                      isQADragging={isQADragging}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
          {/* カテゴリの追加/削除 */}
          <div className="cat-ctrl">
            <button type="button" onClick={() => handleAddGroup(index)}>
              <i className="icon add"></i>
              <span>Add New Group</span>
            </button>
            <button
              type="button"
              onClick={() => handleDeleteGroup(index)}
              disabled={pageContent.length <= 1}
              style={{
                opacity: pageContent.length <= 1 ? 0.5 : 1,
                cursor: pageContent.length <= 1 ? "not-allowed" : "pointer",
              }}
            >
              <i className="icon delete"></i>
              <span>Delete Group</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function QaEditPageContent() {
  const contentKey = "qaContent"; // ページのキー
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [studioList, setStudioList] = useState([]); // スタジオリスト

  // 配列として初期化
  const [pageContent, setPageContent] = useState([]);
  // 各Q&Aアイテムの画像表示状態を管理
  const [imageFormVisible, setImageFormVisible] = useState({});
  // 削除されたカテゴリーとQ&AのIDを管理
  const [deletedCategoryIds, setDeletedCategoryIds] = useState([]);
  const [deletedQAIds, setDeletedQAIds] = useState([]);
  // ドラッグ状態を管理
  const [isDragging, setIsDragging] = useState(false);
  // Q&Aアイテムのドラッグ状態を管理
  const [isQADragging, setIsQADragging] = useState(false);

  // ドラッグ&ドロップのセンサー
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      
      setLoadingText("アップロード中");
      
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
      
      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };
          
          // メディアファイル一覧を再取得
          await fetchMediaFiles();
          
          // fetchMediaFiles完了後に選択状態にする
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // スタジオリストの取得
  const fetchStudiosList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStudioList(data);
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
    }
  }, []);

  // Q&A詳細の取得
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_question_category_list/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      console.log("Fetched Q&A data:", data);

      // データをorder順でソートしてからセット
      const sortedData = data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setPageContent(sortedData);
      setLoadingText("");
      return sortedData; // 取得したデータを返す
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
      setLoadingText("");
      return []; // エラー時は空配列を返す
    }
  }, []);

  useEffect(() => {
    fetchStudiosList(); // スタジオリストの取得
    fetchPageContentData(); // Q&A詳細の取得
    fetchMediaFiles(); // 画像動画リストの取得
  }, [fetchStudiosList, fetchPageContentData, fetchMediaFiles]);

  // データをAPIと同じ形式にフォーマットする関数
  const formatQAData = useCallback((content) => {
    // ソートはせず、管理画面の表示順序をそのまま維持
    return content.map((category) => ({
      id: category.id,
      category: category.category || category.title,
      order: category.order,
      faqs: (category.qa_items || []).map((qa) => ({
        id: qa.id,
        question: qa.q || qa.question,
        answer: qa.a || qa.answer,
        order: qa.order,
        images: (qa.images || [])
          .filter((img) => img && (img.image_url || img.url)) // 有効な画像のみ
          .map((img) => ({
            id: img.id,
            url: img.image_url || img.url,
          })),
        urls: qa.urls || [],
      })),
    }));
  }, []);

  // iframeにデータを送信する共通関数
  const sendToIframe = useCallback(
    (data, messageType = "TEXT_UPDATE") => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        const formattedData = formatQAData(data);
        iframe.contentWindow.postMessage(
          {
            type: messageType,
            contentKey: "qaContent",
            qaCategories: formattedData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    },
    [formatQAData]
  );

  // バリデーションルールを定義
  const validationRules = {
    category: { required: true },
    question: { required: true },
    answer: { required: true },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    category: "カテゴリ名",
    question: "質問",
    answer: "回答",
  };

  // 送信処理
  const handleSubmit = async () => {
    // バリデーション実行
    if (!executeQAValidation(pageContent, validationRules, fieldLabels, setMessage)) {
      return;
    }

    setLoadingText("保存中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // 更新データの準備
      const updateData = {
        categories: pageContent.map((category) => ({
          id: category.id,
          category: category.category || category.title,
          order: category.order,
          qa_items: (category.qa_items || []).map((qa_item) => ({
            id: qa_item.id,
            question: qa_item.q || qa_item.question,
            answer: qa_item.a || qa_item.answer,
            order: qa_item.order,
            // 画像IDの配列に変換
            image_ids: qa_item.images
              ? qa_item.images
                  .map((img) => img.id)
                  .filter((id) => id !== null && id !== undefined)
              : [],
            // URLsデータを追加
            urls: qa_item.urls || [],
            // スタジオIDを追加
            studio_ids: qa_item.studio_ids || [],
          })),
        })),
        // 削除されたIDを追加
        deleted_category_ids: deletedCategoryIds,
        deleted_qa_ids: deletedQAIds,
      };

      console.log("Submitting data:", updateData);

      const response = await fetch(`${apiUrl}/api/update_qa_categories/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "データの更新に失敗しました");
      }

      const data = await response.json();
      setMessage(data.message || "Q&A情報を更新しました");

      // 最新データを再取得
      await fetchPageContentData();
      setHasChanges(false);

      // 削除IDリストをクリア
      setDeletedCategoryIds([]);
      setDeletedQAIds([]);

      // iframeにQ&Aデータを送信
      sendToIframe(pageContent);
    } catch (error) {
      console.error("更新中にエラーが発生しました:", error);
      setMessage(`エラー: ${error.message}`);
    }

    setLoadingText("");
  };

  // リセット処理
  const handleReset = useCallback(async () => {
    handleQAResetWithValidation(async () => {
      setLoadingText("リセット中");

      try {
        // データ再取得
        const freshData = await fetchPageContentData();
        setHasChanges(false);

        // 削除IDリストをクリア
        setDeletedCategoryIds([]);
        setDeletedQAIds([]);

        // 画像表示状態もクリア
        setImageFormVisible({});

        // iframeプレビューを更新（取得した最新データを使用）
        sendToIframe(freshData);
      } catch (error) {
        console.error("リセット中にエラーが発生しました:", error);
      }

      setLoadingText("");
    });
  }, [fetchPageContentData, sendToIframe]);

  // 入力変更ハンドラー
  const handleInputChange = (e, categoryIndex, qaIndex = null, field) => {
    const { value } = e.target;
    const eventType = e.type; // "change" or "focus"

    // バリデーションエラークリア処理
    handleQAInputChangeWithValidation(e, categoryIndex, qaIndex, field);

    setPageContent((prev) => {
      const newContent = [...prev];

      if (qaIndex !== null) {
        // Q&A項目の変更
        if (!newContent[categoryIndex].qa_items) {
          newContent[categoryIndex].qa_items = [];
        }
        if (!newContent[categoryIndex].qa_items[qaIndex]) {
          newContent[categoryIndex].qa_items[qaIndex] = {};
        }
        newContent[categoryIndex].qa_items[qaIndex] = {
          ...newContent[categoryIndex].qa_items[qaIndex],
          [field]: value,
        };
      } else {
        // カテゴリーの変更
        newContent[categoryIndex] = {
          ...newContent[categoryIndex],
          [field]: value,
          title: field === "category" ? value : newContent[categoryIndex].title,
        };
      }

      // iframeにリアルタイム更新を送信
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        // フィールドごとに適切なクラス名を決定
        let targetClass = "";
        if (qaIndex !== null) {
          // Q&A項目の場合
          if (field === "q" || field === "question") {
            targetClass = `.edit_question_${categoryIndex}_${qaIndex}`;
          } else if (field === "a" || field === "answer") {
            targetClass = `.edit_answer_${categoryIndex}_${qaIndex}`;
          }
        } else {
          // カテゴリーの場合
          if (field === "category") {
            targetClass = `.edit_category_${categoryIndex}`;
          }
        }

        // データ更新メッセージを送信
        sendToIframe(newContent);

        // フォーカス時は即座にスクロール、変更時は少し遅延
        if (targetClass) {
          const delay = eventType === "focus" ? 0 : 100;
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: "SCROLL_TO",
                target: targetClass,
                contentKey: "qaContent",
                categoryIndex,
                qaIndex,
                field,
                eventType,
              },
              process.env.REACT_APP_ORIGIN
            );
          }, delay);
        }
      }

      return newContent;
    });

    if (eventType === "change") {
      setHasChanges(true);
    }
  };

  // ドラッグ開始時のハンドラー
  const handleDragStart = () => {
    setIsDragging(true);
  };

  // ドラッグ終了時のハンドラー（セクション用）
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setIsDragging(false);

    if (active.id !== over.id) {
      setPageContent((prev) => {
        const oldIndex = prev.findIndex(
          (item) => (item.id || `section-${prev.indexOf(item)}`) === active.id
        );
        const newIndex = prev.findIndex(
          (item) => (item.id || `section-${prev.indexOf(item)}`) === over.id
        );

        const newContent = arrayMove(prev, oldIndex, newIndex);

        // orderを再設定
        newContent.forEach((item, index) => {
          item.order = index + 1;
        });

        // iframeに並び順変更を通知
        setTimeout(() => {
          sendToIframe(newContent);
        }, 50);

        setHasChanges(true);
        return newContent;
      });
    }
  };

  // Q&Aアイテムのドラッグ開始時のハンドラー
  const handleQADragStart = () => {
    setIsQADragging(true);
  };

  // Q&Aアイテムのドラッグ終了時のハンドラー
  const handleQADragEnd = (event, categoryIndex) => {
    const { active, over } = event;
    setIsQADragging(false);

    if (active?.id !== over?.id) {
      setPageContent((prev) => {
        const newContent = [...prev];
        const qaItems = newContent[categoryIndex].qa_items || [];

        const oldIndex = qaItems.findIndex(
          (item) =>
            (item.id || `qa-${categoryIndex}-${qaItems.indexOf(item)}`) ===
            active.id
        );
        const newIndex = qaItems.findIndex(
          (item) =>
            (item.id || `qa-${categoryIndex}-${qaItems.indexOf(item)}`) ===
            over.id
        );

        // Q&Aアイテムの並び替え
        const newQAItems = arrayMove(qaItems, oldIndex, newIndex);

        // orderを再設定
        newQAItems.forEach((item, index) => {
          item.order = index + 1;
        });

        newContent[categoryIndex].qa_items = newQAItems;

        // iframeにQ&Aアイテムの並び順変更を通知
        setTimeout(() => {
          sendToIframe(newContent);
        }, 50);

        setHasChanges(true);
        return newContent;
      });
    }
  };

  // 新しいカテゴリーを追加（指定位置に挿入）
  const handleAddGroup = (insertAfterIndex = null) => {
    // 挿入位置を決定
    let insertOrder;
    if (insertAfterIndex === null) {
      // 最後尾に追加
      insertOrder = pageContent.length + 1;
    } else {
      // 指定位置の後に挿入
      insertOrder = pageContent[insertAfterIndex].order + 1;
    }

    // 新しいカテゴリーに最低1つのQ&A項目を追加
    const initialQA = {
      id: -Date.now() - 1, // カテゴリーIDと重複しないように-1
      q: "",
      a: "",
      question: "",
      answer: "",
      order: 1,
      images: [],
      urls: [],
      studio_ids: [],
    };

    const newCategory = {
      id: -Date.now(), // 一時的な負のID
      category: "",
      title: "",
      order: insertOrder,
      qa: [initialQA],
      qa_items: [initialQA],
    };

    setPageContent((prev) => {
      const newContent = [...prev];

      if (insertAfterIndex !== null) {
        // 挿入位置より後のカテゴリーのorderを1つずつ繰り上げ
        newContent.forEach((item) => {
          if (item.order >= insertOrder) {
            item.order += 1;
          }
        });
      }

      // 新しいカテゴリーを追加
      newContent.push(newCategory);

      // order順でソート
      const sortedContent = newContent.sort((a, b) => a.order - b.order);

      // iframeにカテゴリー追加を送信
      sendToIframe(sortedContent, "ADD_QA_CATEGORY");

      return sortedContent;
    });
    setHasChanges(true);
  };

  // カテゴリーを削除
  const handleDeleteGroup = (categoryIndex) => {
    if (pageContent.length <= 1) {
      alert("最低1つのカテゴリーは必要です。");
      return;
    }

    const categoryToDelete = pageContent[categoryIndex];
    const categoryName =
      categoryToDelete?.category || categoryToDelete?.title || "このカテゴリー";
    if (
      window.confirm(
        `${categoryName}を削除しますか？このカテゴリー内のすべてのQ&Aも削除されます。`
      )
    ) {
      const deletedOrder = categoryToDelete.order;

      // 既存のカテゴリーの場合、削除IDリストに追加
      if (categoryToDelete.id && categoryToDelete.id > 0) {
        setDeletedCategoryIds((prev) => [...prev, categoryToDelete.id]);

        // このカテゴリー内のQ&Aも削除IDリストに追加
        const qaItems = categoryToDelete.qa_items || [];
        const qaIdsToDelete = qaItems
          .filter((qa) => qa.id && qa.id > 0)
          .map((qa) => qa.id);
        if (qaIdsToDelete.length > 0) {
          setDeletedQAIds((prev) => [...prev, ...qaIdsToDelete]);
        }
      }

      setPageContent((prev) => {
        // 削除対象を除いた配列を作成
        const newContent = prev.filter((_, index) => index !== categoryIndex);

        // 削除されたorderより大きいorderを1つずつ減らす
        newContent.forEach((item) => {
          if (item.order > deletedOrder) {
            item.order -= 1;
          }
        });

        // order順でソート
        const sortedContent = newContent.sort((a, b) => a.order - b.order);

        // iframeにカテゴリー削除を送信
        sendToIframe(sortedContent, "DELETE_QA_CATEGORY");

        return sortedContent;
      });
      setHasChanges(true);
    }
  };

  // 新しいQ&Aを追加
  const handleAddQA = (categoryIndex, qaIndex = null) => {
    const insertIndex =
      qaIndex !== null
        ? qaIndex + 1
        : pageContent[categoryIndex]?.qa_items?.length || 0;

    const newQA = {
      id: -Date.now(),
      q: "",
      a: "",
      question: "",
      answer: "",
      order: insertIndex + 1,
      images: [],
      studio_ids: [],
    };

    setPageContent((prev) => {
      const newContent = [...prev];
      if (!newContent[categoryIndex].qa_items) {
        newContent[categoryIndex].qa_items = [];
      }

      // 指定位置に挿入
      if (qaIndex !== null) {
        newContent[categoryIndex].qa_items.splice(insertIndex, 0, newQA);

        // 挿入位置以降のorderを調整
        newContent[categoryIndex].qa_items.forEach((item, idx) => {
          item.order = idx + 1;
        });
      } else {
        // 末尾に追加（従来の動作）
        newContent[categoryIndex].qa_items.push(newQA);
      }

      // iframeにQ&A項目追加を送信
      sendToIframe(newContent, "ADD_QA_ITEM");

      return newContent;
    });
    setHasChanges(true);
  };

  // Q&Aを削除
  const handleDeleteQA = (categoryIndex, qaIndex) => {
    const qaToDelete = pageContent[categoryIndex]?.qa_items?.[qaIndex];

    // 既存のQ&Aの場合、削除IDリストに追加
    if (qaToDelete?.id && qaToDelete.id > 0) {
      setDeletedQAIds((prev) => [...prev, qaToDelete.id]);
    }

    setPageContent((prev) => {
      const newContent = [...prev];
      if (newContent[categoryIndex].qa_items) {
        newContent[categoryIndex].qa_items.splice(qaIndex, 1);
      }

      // iframeにQ&A項目削除を送信
      sendToIframe(newContent, "DELETE_QA_ITEM");

      return newContent;
    });
    setHasChanges(true);
  };

  // +Imageボタンクリック時の処理
  const handleImageButtonClick = (categoryIndex, qaIndex) => {
    const key = `${categoryIndex}_${qaIndex}`;
    setImageFormVisible((prev) => ({
      ...prev,
      [key]: true,
    }));

    // 画像ボタンクリック時にQ&A項目をアクティブにする
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SCROLL_TO",
          target: `.edit_answer_${categoryIndex}_${qaIndex}`,
          contentKey: "qaContent",
          categoryIndex,
          qaIndex,
          field: "a", // 画像はanswerセクションの一部として扱う
          eventType: "focus",
        },
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // +Linkボタンクリック時の処理（URLを追加）
  const handleAddLink = useCallback(
    (categoryIndex, qaIndex) => {
      setPageContent((prev) => {
        const newContent = [...prev];
        if (!newContent[categoryIndex].qa_items) {
          newContent[categoryIndex].qa_items = [];
        }
        if (!newContent[categoryIndex].qa_items[qaIndex]) {
          newContent[categoryIndex].qa_items[qaIndex] = {};
        }
        if (!newContent[categoryIndex].qa_items[qaIndex].urls) {
          newContent[categoryIndex].qa_items[qaIndex].urls = [];
        }

        // 新しいURLエントリを追加
        newContent[categoryIndex].qa_items[qaIndex].urls.push({
          text: "",
          url: "",
        });

        // iframeにリンク追加を送信
        sendToIframe(newContent);

        return newContent;
      });

      // リンク追加時にQ&A項目をアクティブにする
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "SCROLL_TO",
            target: `.edit_answer_${categoryIndex}_${qaIndex}`,
            contentKey: "qaContent",
            categoryIndex,
            qaIndex,
            field: "a", // URLはanswerセクションの一部として扱う
            eventType: "focus",
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      setHasChanges(true);
    },
    [sendToIframe]
  );

  // URLを削除
  const handleDeleteLink = (categoryIndex, qaIndex, linkIndex) => {
    setPageContent((prev) => {
      const newContent = [...prev];
      if (
        newContent[categoryIndex].qa_items &&
        newContent[categoryIndex].qa_items[qaIndex] &&
        newContent[categoryIndex].qa_items[qaIndex].urls
      ) {
        newContent[categoryIndex].qa_items[qaIndex].urls.splice(linkIndex, 1);
      }

      // iframeにリンク削除を送信
      sendToIframe(newContent);

      return newContent;
    });
    setHasChanges(true);
  };

  // URLフォーカスハンドラー
  const handleUrlFocus = (categoryIndex, qaIndex) => {
    // Q&A項目をアクティブにするためにiframeに通知
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SCROLL_TO",
          target: `.edit_answer_${categoryIndex}_${qaIndex}`,
          contentKey: "qaContent",
          categoryIndex,
          qaIndex,
          field: "a", // URLはanswerセクションの一部として扱う
          eventType: "focus",
        },
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // URL入力変更ハンドラー
  const handleUrlInputChange = (
    e,
    categoryIndex,
    qaIndex,
    linkIndex,
    field
  ) => {
    const value = e.target.value;
    setPageContent((prev) => {
      const newContent = [...prev];
      if (!newContent[categoryIndex].qa_items) {
        newContent[categoryIndex].qa_items = [];
      }
      if (!newContent[categoryIndex].qa_items[qaIndex]) {
        newContent[categoryIndex].qa_items[qaIndex] = {};
      }
      if (!newContent[categoryIndex].qa_items[qaIndex].urls) {
        newContent[categoryIndex].qa_items[qaIndex].urls = [];
      }
      if (!newContent[categoryIndex].qa_items[qaIndex].urls[linkIndex]) {
        newContent[categoryIndex].qa_items[qaIndex].urls[linkIndex] = {
          text: "",
          url: "",
        };
      }

      newContent[categoryIndex].qa_items[qaIndex].urls[linkIndex][field] =
        value;

      // iframeにURL変更を送信
      sendToIframe(newContent);

      return newContent;
    });
    setHasChanges(true);
  };

  // Q&A画像変更ハンドラー
  const handleQAImagesChange = useCallback(
    (categoryIndex, qaIndex, images) => {
      setPageContent((prev) => {
        const newContent = [...prev];
        if (!newContent[categoryIndex].qa_items) {
          newContent[categoryIndex].qa_items = [];
        }
        if (!newContent[categoryIndex].qa_items[qaIndex]) {
          newContent[categoryIndex].qa_items[qaIndex] = {};
        }
        newContent[categoryIndex].qa_items[qaIndex] = {
          ...newContent[categoryIndex].qa_items[qaIndex],
          images: images,
        };

        // iframeに画像変更を送信
        sendToIframe(newContent);

        return newContent;
      });

      // 画像選択時にQ&A項目をアクティブにする
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "SCROLL_TO",
            target: `.edit_answer_${categoryIndex}_${qaIndex}`,
            contentKey: "qaContent",
            categoryIndex,
            qaIndex,
            field: "a", // 画像はanswerセクションの一部として扱う
            eventType: "focus",
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      // 画像が0枚になった場合は.img-formを非表示にする
      const key = `${categoryIndex}_${qaIndex}`;
      if (images.length === 0) {
        setImageFormVisible((prev) => ({
          ...prev,
          [key]: false,
        }));
      }

      setHasChanges(true);
    },
    [sendToIframe]
  );

  // スタジオ選択トグルハンドラー
  const handleStudioToggle = (categoryIndex, qaIndex, studioId) => {
    setPageContent((prev) => {
      const newContent = [...prev];
      if (!newContent[categoryIndex].qa_items) {
        newContent[categoryIndex].qa_items = [];
      }
      if (!newContent[categoryIndex].qa_items[qaIndex]) {
        newContent[categoryIndex].qa_items[qaIndex] = {};
      }

      const currentStudioIds =
        newContent[categoryIndex].qa_items[qaIndex].studio_ids || [];
      let newStudioIds;

      if (currentStudioIds.includes(studioId)) {
        // 既に選択されている場合は削除
        newStudioIds = currentStudioIds.filter((id) => id !== studioId);
      } else {
        // 選択されていない場合は追加
        newStudioIds = [...currentStudioIds, studioId];
      }

      newContent[categoryIndex].qa_items[qaIndex] = {
        ...newContent[categoryIndex].qa_items[qaIndex],
        studio_ids: newStudioIds,
      };

      return newContent;
    });

    setHasChanges(true);
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            <div className="form">
              {pageContent.length === 0 ? (
                <section>
                  <div className="area4">
                    <div className="item">
                      <p>
                        Q&Aカテゴリーがありません。「Add New
                        Group」ボタンで新しいカテゴリーを追加してください。
                      </p>
                      <div className="cat-ctrl">
                        <button type="button" onClick={() => handleAddGroup()}>
                          <i className="icon add"></i>
                          <span>Add New Group</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={pageContent.map(
                      (item, index) => item.id || `section-${index}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    {[...pageContent]
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((item, index) => (
                        <SortableQASection
                          key={item.id || `section-${index}`}
                          item={item}
                          index={index}
                          handleInputChange={handleInputChange}
                          handleDeleteGroup={handleDeleteGroup}
                          handleAddGroup={handleAddGroup}
                          handleDeleteQA={handleDeleteQA}
                          handleAddQA={handleAddQA}
                          handleImageButtonClick={handleImageButtonClick}
                          handleAddLink={handleAddLink}
                          handleDeleteLink={handleDeleteLink}
                          handleUrlFocus={handleUrlFocus}
                          handleUrlInputChange={handleUrlInputChange}
                          handleQAImagesChange={handleQAImagesChange}
                          handleStudioToggle={handleStudioToggle}
                          pageContent={pageContent}
                          imageFormVisible={imageFormVisible}
                          studioList={studioList}
                          isDragging={isDragging}
                          handleQADragEnd={handleQADragEnd}
                          handleQADragStart={handleQADragStart}
                          isQADragging={isQADragging}
                        />
                      ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={handleReset} disabled={!hasChanges}>
                Reset
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={!hasChanges}
              >
                Submit
              </button>
            </div>

            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      {/* プレビュー */}
      <Iframe path={"/qa"} data={pageContent} contentKey={contentKey} />
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function QaEditPage() {
  return (
    <MediaProvider>
      <QaEditPageContent />
    </MediaProvider>
  );
}
